import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as FileSaver from 'file-saver';
import { finalize, map, Subject, takeUntil } from 'rxjs';
import { type FoodItemOutflowModel } from 'src/app/models/food-item-outflow.model';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/shared/notification.service';
import { DialogConfirmationComponent } from './dialog-confirmation/dialog-confirm.component';

interface Evidence {
  bodyBase64: string;
  updatedAt: string;
  fileType: string;
  key: string;
  bodyUrl?: string;
  evidenceName?: string;
  evidenceNameToDownload?: string;
}

const OUTFLOW_EVIDENCE_ENDPOINT = `${environment.alimentosBebidasApi}/api/v1`;

@Component({
  selector: 'dialog-food-outflow-evidence.component',
  templateUrl: './dialog-evidence.component.html',
  styleUrls: ['./dialog-evidence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFoodOutflowEvidenceComponent implements OnInit, OnDestroy {
  evidenceIndex = 0;
  isLoading = true;
  isUploadingEvidence = false;
  isEvidenceShowing = false;

  evidences: Evidence[] = [];
  evidenceUrl: SafeResourceUrl = '';

  onDestroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public foodOutflowEvidenceDialogRef: MatDialogRef<DialogFoodOutflowEvidenceComponent>,
    public matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: { outflow: FoodItemOutflowModel | undefined | null },
    private readonly httpClient: HttpClient,
    private readonly notificationService: NotificationService,
    private readonly sanitizer: DomSanitizer,
    private readonly cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.httpClient
      .get<{ evidences: Evidence[] }>(
        `${OUTFLOW_EVIDENCE_ENDPOINT}/getEvidencias/${
          this.dialogData?.outflow?.id ?? ''
        }`,
      )
      .pipe(
        takeUntil(this.onDestroy$),
        map((response) => response.evidences),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (evidences) => {
          if (evidences && evidences.length > 0) {
            for (const evidence of evidences) {
              const evidenceFileName = evidence.key.split(';')[1];
              const evidenceNameToDownload = `retiro-${
                this.dialogData?.outflow?.nombre ?? 'Egreso'
              }-${new Date(
                evidence.updatedAt,
              ).toLocaleDateString()}-${evidenceFileName}`;

              const dataUrl = atob(evidence?.bodyBase64 ?? '');
              this.evidences.push({
                bodyUrl: dataUrl,
                bodyBase64: evidence.bodyBase64,
                key: evidence.key,
                fileType: evidence.fileType,
                updatedAt: evidence.updatedAt.toString(),
                evidenceName: evidenceFileName,
                evidenceNameToDownload,
              });
            }
            return;
          }
          this.evidences = [];
        },
        error: (error) => {
          console.error('Error fetching evidences:', error);
          this.evidences = [];
        },
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.handleDialogClose();
  }

  handleDialogClose() {
    this.foodOutflowEvidenceDialogRef.close({
      updatedOutflow: {
        ...this.dialogData.outflow,
        hasDocument: this.evidences.length > 0,
      },
    });
  }

  async uploadEvidence(event: Event) {
    this.isUploadingEvidence = true;
    const target = event.target as HTMLInputElement;
    if (!target.files || (target.files.length === 0 && target.files.length > 1))
      return;
    const FILE_MAX_SIZE = 4_000_000;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
    ];
    const files = Array.from(target.files) as File[];

    if (files[0].size > FILE_MAX_SIZE) {
      this.isUploadingEvidence = false;
      this.notificationService.notifyError(
        'El tamaño del archivo excede el límite permitido de 4 MB.',
      );
      return;
    }

    if (!allowedTypes.includes(files[0].type)) {
      this.isUploadingEvidence = false;
      this.notificationService.notifyError(
        'El formato del archivo no está soportado. Por favor, suba un PDF, Word o una imagen (JPEG/PNG).',
      );
      return;
    }

    if (!allowedTypes.includes(files[0].type)) {
      this.isUploadingEvidence = false;
      this.notificationService.notifyError(
        'El formato del archivo no está soportado. Por favor, suba un PDF, Word o una imagen (JPEG/PNG).',
      );
      return;
    }

    const fileBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result || '');
      };
      reader.onerror = () => {
        reject(new Error(`Failed to read file: ${files[0].name}`));
      };
    });

    this.httpClient
      .post<Evidence>(`${OUTFLOW_EVIDENCE_ENDPOINT}/addFileEvidencia`, {
        fileBase64,
        outflowId: this.dialogData?.outflow?.id,
        evidenceFileName: files[0].name,
        fileType: files[0].type,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.isUploadingEvidence = false;
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (newEvidence) => {
          this.evidences.push({
            ...newEvidence,
            evidenceNameToDownload: `retiro-${
              this.dialogData?.outflow?.nombre ?? 'Egreso'
            }-${new Date(newEvidence.updatedAt).toLocaleDateString()}-${
              files[0].name
            }`,
          });
        },
        error: (error) => {
          console.error('Error uploading evidence:', error);
          this.notificationService.notifyError('Error al subir la evidencia.');
        },
      });
  }

  toggleEvidenceDisplay(evidence: Evidence, index: number) {
    if (index === this.evidenceIndex && this.isEvidenceShowing) {
      this.isEvidenceShowing = false;
      this.evidenceIndex = -1;
      this.evidenceUrl = '';
    } else {
      this.evidenceIndex = index;
      if (
        typeof evidence.bodyUrl === 'string' &&
        evidence.bodyUrl.startsWith('data:')
      ) {
        this.evidenceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          evidence.bodyUrl,
        );
        this.isEvidenceShowing = true;
      } else {
        console.error(
          '[activatePDF] Evidence src is not a valid data URL string:',
          evidence.bodyUrl,
        );
        this.isEvidenceShowing = false;
        this.evidenceUrl = '';
      }
    }
  }

  downloadEvidence(evidence: Evidence) {
    try {
      let base64Data = evidence?.bodyUrl ?? atob(evidence?.bodyBase64 ?? '');
      let type = evidence?.fileType ?? 'application/pdf';
      if (base64Data.includes(',')) {
        base64Data = base64Data.split(',')[1] ?? '';
        type = base64Data.split(',')[0] ?? 'application/pdf';
      }

      const binaryString = atob(base64Data);
      const byteNumbers = new Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteNumbers[i] = binaryString.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type });
      FileSaver.saveAs(
        blob,
        evidence?.evidenceNameToDownload ?? 'evidence.pdf',
      );
    } catch (error) {
      console.error('Error downloading evidence:', error);
      this.notificationService.notifyError('Error al descargar la evidencia.');
    }
  }

  deleteEvidence(evidence: Evidence, index: number) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false;
    dialogConfig.data = {
      outflowId: this.dialogData?.outflow?.insumoId ?? '',
      evidenceKey: evidence.key,
    };

    const dialogRef = this.matDialog.open(
      DialogConfirmationComponent,
      dialogConfig,
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: { deletedSuccessfully: boolean }) => {
        if (!!result && result.deletedSuccessfully) {
          this.notificationService.showSuccessAlert(
            'La evidencia se eliminó con éxito.',
          );
          this.evidences.splice(index, 1);
        }
      });
  }
}
